import { CalendarOutlined, FilterOutlined, ReloadOutlined, EllipsisOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Button, Card, Checkbox, DatePicker, Dropdown, Form, Input, message, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useContext, useState } from 'react';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { Transaction } from '../../types/transaction.types';
import { handleAPIError, formatNumber } from '../../utils/helpers';
import CopyableText from '../../components/copyable-text';
import { UserMinimal } from '../../types/user.types';
import { AuthContext } from '../../context/auth.context';

const { RangePicker } = DatePicker;

interface FilterValues {
	status: string | null;
	user: number | null;
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
	type: string | null;
	gateway: string | null;
	gateway_trx_id: string | null;
	double_payment: boolean | null;
}

// What arrived on an installment after it was already settled, if anything.
const doublePaid = (transaction: Transaction) =>
	(transaction.metadata as { double_payment?: string } | null)?.double_payment;

const Transactions = () => {
	const { user } = useContext(AuthContext);
	const [form] = Form.useForm<FilterValues>();
	const [paginationConfig, setPaginationConfig] = useState({
		page: 1,
		pageSize: 10,
	});
	const [count, setCount] = useState(0);
	const [filters, setFilters] = useState<FilterValues>({
		status: null,
		user: null,
		date_range: null,
		type: null,
		gateway: null,
		gateway_trx_id: null,
		double_payment: null,
	});

	const { data, error, isLoading, refetch } = useQuery({
		queryKey: ['transactions', paginationConfig.page, paginationConfig.pageSize, filters],
		queryFn: async () => {
			const params = {
				page: paginationConfig.page,
				limit: paginationConfig.pageSize,
				status: filters.status,
				user: filters.user,
				type: filters.type,
				gateway: filters.gateway,
				gateway_trx_id: filters.gateway_trx_id,
				double_payment: filters.double_payment ? 'true' : null,
				start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
				end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
			};

			const response = await axios.get('/admin/transactions', {
				withCredentials: true,
				params,
			});
			setCount(response.data.count);
			return response.data;
		},
	});

	if (error) {
		handleAPIError(error);
	}

	const markTransactionAsSuccess = async (id: number) => {
		try {
			message.loading('Marking transaction as success...');
			await axios.patch(
				`/admin/payments/transactions/${id}/mark-success`,
				{},
				{
					withCredentials: true,
				},
			);
			message.destroy();
			message.success('Transaction marked as success');
			// Refetch the transactions data
			await refetch();
		} catch (error) {
			handleAPIError(error as Error);
		}
	};

	const transactions: Transaction[] = data?.results || [];

	const columns: ColumnsType<Transaction> = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			render: (type) => {
				return (
					<Tag color={type === 'installment_payment' ? 'blue' : 'green'}>
						{type === 'installment_payment' ? 'Installment Payment' : 'Retailer Withdrawal'}
					</Tag>
				);
			},
		},
		{
			title: 'Amount',
			dataIndex: 'amount',
			key: 'amount',
			render: (amount) => {
				return <span className="">{formatNumber(amount)} UGX</span>;
			},
		},
		{
			title: 'User',
			dataIndex: 'user',
			key: 'user',
			render: (user: UserMinimal) => (
				<div className="flex items-center gap-2">
					<Avatar src={user.dp} />
					<div>
						<span className="text-sm font-medium uppercase">
							{user.first_name} {user.last_name}
						</span>
						<CopyableText text={user.phone_number}>
							<a
								className="text-sm text-gray-500"
								href={`tel:+${user.phone_number}`}
							>
								{user.phone_number}
							</a>
						</CopyableText>
					</div>
				</div>
			),
		},
		{
			title: 'Details',
			dataIndex: 'id',
			key: 'details',
			render: (_, transaction: Transaction) => {
				return (
					<div className="rounded-md border border-gray-200 bg-gray-50 p-3">
						<div className="flex flex-col gap-3">
							{doublePaid(transaction) && (
								<div className="flex items-center justify-between">
									<Typography.Text strong>Double payment:</Typography.Text>
									<Tag color="red">UGX {formatNumber(doublePaid(transaction)!)}</Tag>
								</div>
							)}
							<div className="flex items-center justify-between">
								<Typography.Text strong>Status:</Typography.Text>
								<Tag
									color={
										transaction.status === 'success'
											? 'green'
											: transaction.status === 'pending'
												? 'orange'
												: transaction.status === 'failed'
													? 'red'
													: 'default'
									}
								>
									{transaction.status?.toUpperCase()}
								</Tag>
							</div>

							<div className="flex items-center justify-between">
								<Typography.Text strong>Gateway:</Typography.Text>
								<Tag
									color={
										transaction.gateway === 'beyonic'
											? 'purple'
											: transaction.gateway === 'yo'
												? 'blue'
												: transaction.gateway === 'cash in hand'
													? 'green'
													: 'default'
									}
								>
									{transaction.gateway?.replace('_', ' ').toUpperCase()}
								</Tag>
							</div>

							<div className="flex items-center justify-between">
								<Typography.Text strong>Gateway TRX ID:</Typography.Text>
								{(() => {
									if (transaction.gateway === 'beyonic') {
										return transaction.gateway_trx_id ? (
											<CopyableText text={transaction.gateway_trx_id.toString()}>
												<Typography.Text code>{transaction.gateway_trx_id}</Typography.Text>
											</CopyableText>
										) : (
											<Typography.Text type="secondary">N/A</Typography.Text>
										);
									} else if (transaction.gateway === 'yo') {
										const yoReference = (transaction.metadata as { yo_external_reference?: string })?.yo_external_reference;
										return yoReference ? (
											<CopyableText text={yoReference.toString()}>
												<Typography.Text code>{yoReference}</Typography.Text>
											</CopyableText>
										) : (
											<Typography.Text type="secondary">N/A</Typography.Text>
										);
									} else {
										return transaction.gateway_trx_id ? (
											<CopyableText text={transaction.gateway_trx_id.toString()}>
												<Typography.Text code>{transaction.gateway_trx_id}</Typography.Text>
											</CopyableText>
										) : (
											<Typography.Text type="secondary">N/A</Typography.Text>
										);
									}
								})()}
							</div>

							<div className="flex items-center justify-between">
								<Typography.Text strong>Date:</Typography.Text>
								<div className="flex items-center gap-1">
									<CalendarOutlined className="text-gray-400" />
									<Typography.Text type="secondary">{dayjs(transaction.date).format('DD MMM YYYY - LT')}</Typography.Text>
								</div>
							</div>
						</div>
					</div>
				);
			},
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 70,
			render: (_, transaction: Transaction) => {
				const isSuccess = transaction.status === 'success';

				const items = [
					{
						key: 'mark_success',
						label: 'Mark TRX As Success',
						disabled: isSuccess,
						onClick: () => markTransactionAsSuccess(transaction.id),
					},
				];

				return (
					<Dropdown
						menu={{ items }}
						trigger={['hover']}
						disabled={isSuccess || user?.admin_type !== 'super_admin'}
					>
						<Button
							type="text"
							icon={<EllipsisOutlined />}
						/>
					</Dropdown>
				);
			},
		},
	];

	const handleFilterApply = (values: FilterValues) => {
		setFilters(values);
		setPaginationConfig({
			...paginationConfig,
			page: 1, // Reset to first page when filtering
		});
	};

	const handleFilterReset = () => {
		form.resetFields();
		setFilters({
			status: null,
			user: null,
			date_range: null,
			type: null,
			gateway: null,
			gateway_trx_id: null,
			double_payment: null,
		});
		setPaginationConfig({
			...paginationConfig,
			page: 1,
		});
	};

	const statusOptions = [
		{ value: 'success', label: 'Success' },
		{ value: 'pending', label: 'Pending' },
		{ value: 'failed', label: 'Failed' },
	];

	const typeOptions = [
		{ value: 'installment_payment', label: 'Installment Payment' },
		{ value: 'retailer_withdrawal', label: 'Retailer Withdrawal' },
	];

	const gatewayOptions = [
		{ value: 'cash in hand', label: 'Cash in Hand' },
		{ value: 'beyonic', label: 'Beyonic' },
		{ value: 'yo', label: 'Yo' },
	];

	return (
		<AppLayout
			title="Transactions"
			subtitle="View and manage all transactions."
		>
			<Card className="mb-4 shadow-sm">
				<Form
					form={form}
					layout="horizontal"
					onFinish={handleFilterApply}
					initialValues={filters}
				>
					<div className="flex flex-wrap items-end gap-4">
						<Form.Item
							name="type"
							label="Transaction Type"
							className="mb-0 min-w-[200px]"
						>
							<Select
								placeholder="Select type"
								allowClear
								options={typeOptions}
							/>
						</Form.Item>

						<Form.Item
							name="status"
							label="Status"
							className="mb-0 min-w-[150px]"
						>
							<Select
								placeholder="Select status"
								allowClear
								options={statusOptions}
							/>
						</Form.Item>

						<Form.Item
							name="double_payment"
							valuePropName="checked"
							className="mb-0"
						>
							<Checkbox>Double payments</Checkbox>
						</Form.Item>

						<Form.Item
							name="gateway"
							label="Payment Gateway"
							className="mb-0 min-w-[180px]"
						>
							<Select
								placeholder="Select gateway"
								allowClear
								options={gatewayOptions}
							/>
						</Form.Item>

						<Form.Item
							name="gateway_trx_id"
							label="Gateway Transaction ID"
							className="mb-0 min-w-[200px]"
						>
							<Input
								placeholder="Enter gateway transaction ID"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="user"
							label="User ID"
							className="mb-0 min-w-[150px]"
						>
							<Input
								placeholder="User ID"
								type="number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="date_range"
							label="Date Range"
							className="mb-0 min-w-[300px]"
						>
							<RangePicker
								allowClear
								format="YYYY-MM-DD"
								placeholder={['Start Date', 'End Date']}
							/>
						</Form.Item>

						<Form.Item className="mb-0 flex-shrink-0">
							<Space>
								<Button
									type="primary"
									htmlType="submit"
									icon={<FilterOutlined />}
								>
									Apply Filters
								</Button>
								<Button
									onClick={handleFilterReset}
									icon={<ReloadOutlined />}
								>
									Reset
								</Button>
							</Space>
						</Form.Item>
					</div>
				</Form>
			</Card>

			<div className="mb-20">
				<Table
					columns={columns}
					dataSource={transactions}
					rowKey="id"
					pagination={{
						total: count,
						pageSize: paginationConfig.pageSize,
						current: paginationConfig.page,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						onChange: (page, pageSize) => {
							setPaginationConfig({ page, pageSize });
						},
					}}
					loading={isLoading}
				/>
			</div>
		</AppLayout>
	);
};

export default Transactions;
